import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  Injector,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { Observable, of, combineLatest } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { toObservable } from '@angular/core/rxjs-interop';

interface AutocompleteOption {
  value: string;
  displayValue?: string;
}

interface AutocompleteEditorParams {
  placeholder: string;
  values: () => AutocompleteOption[];
  loadingData: () => boolean;
  loadOptions: () => void;
  filterStartsWith?: boolean;
}

@Component({
  selector: 'lib-autocomplete-editor',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatInputModule,
  ],
  templateUrl: './autocomplete-editor.component.html',
  styleUrls: ['./autocomplete-editor.component.scss'],
})
export class AutocompleteEditorComponent
  implements ICellEditorAngularComp, OnDestroy
{
  private readonly injector = inject(Injector);
  private params!: ICellEditorParams & AutocompleteEditorParams;
  @ViewChild('input') private readonly input!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger)
  private readonly trigger!: MatAutocompleteTrigger;

  public myControl = new FormControl<string | AutocompleteOption | null>('');
  public placeholder!: string;
  public filteredOptions: Observable<AutocompleteOption[]> = of([]);
  public isPanelOpen = false;
  public panelWidth = 0;
  public cellWidth = 0;
  public cellHeight = 0;
  public filterStartsWith = false;

  public selectOptions = computed<AutocompleteOption[]>(() => {
    if (!this.params) return [];
    return this.params.values();
  });
  public loader = computed<boolean>(() => {
    if (!this.params) return false;
    return this.params.loadingData();
  });

  private onBodyScrollListener: (() => void) | undefined;

  public agInit(params: ICellEditorParams & AutocompleteEditorParams): void {
    this.params = params;
    this.placeholder = this.params.placeholder || '';
    this.filterStartsWith = this.params.filterStartsWith || false;

    const cell = params.eGridCell;
    this.cellWidth = cell.clientWidth + 4;
    this.cellHeight = cell.clientHeight + 4;

    this.myControl.setValue(this.params.value);

    const selectOptions$ = toObservable(this.selectOptions, {
      injector: this.injector,
    });

    this.filteredOptions = combineLatest([
      this.myControl.valueChanges.pipe(startWith(null)),
      selectOptions$,
    ]).pipe(
      map(([value, options]) => {
        if (!value) return options.slice();
        return this._filter(value, options);
      })
    );

    // When the grid is scrolled, close the autocomplete panel.
    this.onBodyScrollListener = () => {
      if (this.trigger) {
        this.trigger.closePanel();
      }
    };
    this.params.api.addEventListener('bodyScroll', this.onBodyScrollListener);
  }

  ngOnDestroy(): void {
    if (this.onBodyScrollListener) {
      this.params.api.removeEventListener(
        'bodyScroll',
        this.onBodyScrollListener
      );
    }
  }

  public afterGuiAttached(): void {
    this.params.loadOptions();
    this.input.nativeElement.focus();
  }

  public getValue(): string {
    const controlValueAsString = this.controlValueAsString;
    const matchedOption = this.selectOptions().find(
      (option) =>
        option.value.toLowerCase() === controlValueAsString?.toLowerCase()
    );
    return matchedOption ? matchedOption.value : '';
  }

  public onFocus(event: FocusEvent): void {
    const el = event.target as HTMLInputElement;
    this.panelWidth = el.offsetWidth;
    this.trigger.openPanel();
  }

  public onPanelOpen(): void {
    this.isPanelOpen = true;
  }

  public onPanelClose(): void {
    this.isPanelOpen = false;
  }

  public togglePanel(): void {
    if (this.trigger.panelOpen) {
      this.trigger.closePanel();
    } else {
      this.input.nativeElement.focus();
      this.trigger.openPanel();
    }
  }

  public clearInput(event: Event): void {
    this.myControl.setValue('');
    event.stopPropagation();

    setTimeout(() => {
      this.input.nativeElement.focus();
    });
  }

  public selectOption(option: AutocompleteOption): void {
    this.myControl.setValue(option.value);
    this.params.api.stopEditing();
  }

  public onBlur(): void {
    const isValid = this.selectOptions().some(
      (option) =>
        option.value.toLowerCase() === this.controlValueAsString?.toLowerCase()
    );
    if (!isValid) {
      this.myControl.setValue('');
    }
  }

  public get controlValueAsString(): string {
    const controlValue = this.myControl.value;
    if (typeof controlValue === 'string') {
      return controlValue;
    }
    if (controlValue && 'value' in controlValue) {
      return controlValue.value;
    }
    return '';
  }

  private _filter(
    value: string | AutocompleteOption,
    options: AutocompleteOption[]
  ): AutocompleteOption[] {
    const filterValue = (typeof value === 'string' ? value : value?.value || '')
      .trim()
      .toLowerCase();
    return options.filter((option) =>
      this.filterStartsWith
        ? option.value.toLowerCase().startsWith(filterValue)
        : option.value.toLowerCase().includes(filterValue)
    );
  }
}
